import React, { useCallback, useMemo, useState } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { ControlAssociationsEditor } from './ControlAssociationsEditor.tsx';
import { RiskProcessAssociations } from '../../risks/components/RiskProcessAssociations.tsx';
import {
  controlRiskAssociationsFormSchema,
  type ControlRiskAssociationsFormValues,
} from '../schema.ts';
import { useControlRiskFilterOptions } from '../../../hooks/controls/useControlRiskFilterOptions.ts';
import { useControlItgcRiskOptions } from '../../../hooks/controls/useControlItgcRiskOptions.ts';
import { useAuditYear } from '../../../contexts/AuditYearContext.tsx';

type Props = {
  classification?: string;
  itgcDomainId?: string;
  onBusinessRiskMappingsChange?: (mappings: { riskId: string; processIds: string[] }[]) => void;
  onITGCRiskIdsChange?: (riskIds: string[]) => void;
};

export const ControlCreateRiskMitigatedSection: React.FC<Props> = ({
  classification,
  itgcDomainId,
  onBusinessRiskMappingsChange,
  onITGCRiskIdsChange,
}) => {
  const currentClassification = String(classification ?? '').trim().toLowerCase();
  const isITGC = currentClassification === 'itgc';
  const domainId = String(itgcDomainId ?? '').trim();

  const { selectedAuditYearId } = useAuditYear();
  const [riskSearchInput, setRiskSearchInput] = useState('');
  const [itgcRiskSearchInput, setItgcRiskSearchInput] = useState('');
  const [itgcRiskIds, setItgcRiskIds] = useState<string[]>([]);

  const {
    control: formControl,
  } = useForm<ControlRiskAssociationsFormValues>({
    resolver: yupResolver(controlRiskAssociationsFormSchema),
    defaultValues: { riskAssociations: [] },
  });

  const { fields, append, remove } = useFieldArray({
    control: formControl,
    name: 'riskAssociations',
  });
  const watchedRiskAssociations = useWatch({
    control: formControl,
    name: 'riskAssociations',
  });
  const riskAssociations = useMemo(
    () =>
      (watchedRiskAssociations ?? []).map((field) => ({
        riskId: String((field as { riskId?: string }).riskId ?? '').trim(),
        processIds: Array.isArray((field as { mitigatingProcessIds?: string[] }).mitigatingProcessIds)
          ? (field as { mitigatingProcessIds?: string[] }).mitigatingProcessIds.map((id) => String(id).trim()).filter(Boolean)
          : [],
      })),
    [watchedRiskAssociations]
  );

  const riskOptionsQuery = useControlRiskFilterOptions(riskSearchInput, !isITGC, selectedAuditYearId);
  const riskOptions = useMemo(() => {
    const pages = riskOptionsQuery.data?.pages ?? [];
    return pages.flatMap((page) => page.items);
  }, [riskOptionsQuery.data?.pages]);

  const itgcRiskOptionsQuery = useControlItgcRiskOptions(domainId, itgcRiskSearchInput, isITGC);
  const itgcRiskOptions = useMemo(() => {
    const pages = itgcRiskOptionsQuery.data?.pages ?? [];
    return pages.flatMap((page) => page.items);
  }, [itgcRiskOptionsQuery.data?.pages]);

  const loadMoreBusinessRisks = useCallback(() => {
    if (riskOptionsQuery.hasNextPage && !riskOptionsQuery.isFetchingNextPage) {
      void riskOptionsQuery.fetchNextPage();
    }
  }, [riskOptionsQuery]);

  const loadMoreItgcRisks = useCallback(() => {
    if (itgcRiskOptionsQuery.hasNextPage && !itgcRiskOptionsQuery.isFetchingNextPage) {
      void itgcRiskOptionsQuery.fetchNextPage();
    }
  }, [itgcRiskOptionsQuery]);

  const previousDomainIdRef = React.useRef<string>(domainId);
  React.useEffect(() => {
    if (!isITGC) {
      previousDomainIdRef.current = domainId;
      return;
    }
    if (previousDomainIdRef.current === domainId) return;
    previousDomainIdRef.current = domainId;
    setItgcRiskIds([]);
    setItgcRiskSearchInput('');
  }, [domainId, isITGC]);

  React.useEffect(() => {
    if (!onBusinessRiskMappingsChange) return;
    onBusinessRiskMappingsChange(
      riskAssociations
        .filter((item) => item.riskId)
        .map((item) => ({ riskId: item.riskId, processIds: item.processIds }))
    );
  }, [onBusinessRiskMappingsChange, riskAssociations]);

  React.useEffect(() => {
    if (!onITGCRiskIdsChange) return;
    onITGCRiskIdsChange(itgcRiskIds);
  }, [itgcRiskIds, onITGCRiskIdsChange]);

  if (isITGC) {
    return (
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-3 mb-2">
          <label className="block font-medium text-sm text-gray-600">Risk Mitigated</label>
        </div>
        <RiskProcessAssociations
          variant="risk"
          sectionTitle="Risk linked"
          sectionDescription="Add the risks that should reference this ITGC control."
          selectedValues={itgcRiskIds}
          processOptions={itgcRiskOptions}
          selectedLabelsByValue={{}}
          loadingOptions={domainId ? itgcRiskOptionsQuery.isLoading : false}
          loadingMoreOptions={itgcRiskOptionsQuery.isFetchingNextPage}
          searchInputValue={itgcRiskSearchInput}
          hasMoreOptions={Boolean(itgcRiskOptionsQuery.hasNextPage)}
          onSearchChange={setItgcRiskSearchInput}
          onLoadMore={loadMoreItgcRisks}
          readOnly={false}
          placeholderOnBlur={domainId ? '+ Add risk...' : 'Select domain first'}
          placeholderOnFocus={domainId ? 'Search risks...' : 'Select domain first'}
          onAdd={(value) => {
            const next = String(value ?? '').trim();
            if (!next) return;
            setItgcRiskIds((prev) => (prev.includes(next) ? prev : [...prev, next]));
          }}
          onRemove={(value) => {
            const removeId = String(value ?? '').trim();
            setItgcRiskIds((prev) => prev.filter((id) => id !== removeId));
          }}
        />
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-3 mb-2">
        <label className="block font-medium text-sm text-gray-600">Risk Mitigated</label>
      </div>
      <ControlAssociationsEditor
        control={formControl}
        fields={fields}
        append={append}
        remove={remove}
        riskOptions={riskOptions}
        riskLabelsByValue={{}}
        loadingRisks={riskOptionsQuery.isLoading}
        loadingMoreRisks={riskOptionsQuery.isFetchingNextPage}
        riskSearchInput={riskSearchInput}
        hasNextRiskPage={Boolean(riskOptionsQuery.hasNextPage)}
        onRiskSearchChange={setRiskSearchInput}
        onLoadMoreRisks={loadMoreBusinessRisks}
        processLabelsByValue={{}}
        riskMappings={[]}
        readOnly={false}
      />
    </div>
  );
};

export default ControlCreateRiskMitigatedSection;
